/**
 * ContributorsTooltip Component
 *
 * Displays a contributors icon with a badge showing total count.
 * On hover, shows a tooltip with three categories:
 * - Photographer: The photo owner (always 1)
 * - Critics: Users who have commented
 * - Lurkers: Users who have viewed but not commented
 */

import * as Tooltip from '@radix-ui/react-tooltip';
import { ContributorsIcon } from '../../icons';
import { Avatar } from '../../atoms/Avatar/Avatar';
import { Text } from '../../atoms/Text/Text';
import { useSessionContributorsGraphQL, type ContributorUser } from '../../../graphql/hooks/useCritiquesGraphQL';
import './ContributorsTooltip.css';

export interface ContributorsTooltipProps {
  sessionId: string;
  className?: string;
}

function ContributorRow({ user, isPhotographer = false }: { user: ContributorUser; isPhotographer?: boolean }) {
  return (
    <div className="contributor-row">
      <Avatar
        src={user.avatarUrl || undefined}
        fallback={user.displayName || user.username}
        size="sm"
      />
      <Text className={`contributor-name ${isPhotographer ? 'text-primary' : ''}`}>
        {user.displayName || user.username}
      </Text>
    </div>
  );
}

function ContributorSection({
  label,
  users,
  isPhotographer = false,
}: {
  label: string;
  users: ContributorUser[];
  isPhotographer?: boolean;
}) {
  if (users.length === 0) return null;

  return (
    <div className="contributor-section">
      <Text className="contributor-section-label">{label}</Text>
      <div className="contributor-list">
        {users.map((user) => (
          <ContributorRow key={user.id} user={user} isPhotographer={isPhotographer} />
        ))}
      </div>
    </div>
  );
}

export function ContributorsTooltip({ sessionId, className = '' }: ContributorsTooltipProps) {
  const { data, isLoading } = useSessionContributorsGraphQL(sessionId);

  if (isLoading || !data || !data.photographer) {
    return (
      <div className={`contributors-tooltip-trigger ${className}`}>
        <ContributorsIcon size={48} className="text-muted" />
      </div>
    );
  }

  const { photographer, critics, lurkers, totalCount } = data;

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            className={`contributors-tooltip-trigger ${className}`}
            aria-label={`${totalCount} contributor${totalCount !== 1 ? 's' : ''}`}
          >
            <ContributorsIcon
              size={48}
              count={totalCount}
              className="text-muted hover:text-foreground transition-colors"
            />
          </button>
        </Tooltip.Trigger>

        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            align="start"
            className="contributors-tooltip-content"
            sideOffset={8}
          >
            <ContributorSection
              label="Photographer"
              users={[photographer]}
              isPhotographer
            />
            {critics.length > 0 && (
              <ContributorSection label="Critics" users={critics} />
            )}
            {lurkers.length > 0 && (
              <ContributorSection label="Lurkers" users={lurkers} />
            )}

            <Tooltip.Arrow className="contributors-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
